import AppThemeIcon from "@core-components/icon/AppThemeIcon";
import Icon from "@core-components/icon/Icon";
import { type AppRegion, EAppLolRegions } from "@core-types/region.enum";
import LolRegionHelper from "@helpers/regions/LolRegionHelper";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Button, Checkbox, Group, Image, Indicator, Menu } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";

type Option = {
  label: string;
  value: AppRegion;
};

interface IRegionSelect {
  value: AppRegion;
  onChange: (value: AppRegion) => void;
}

const RegionRankedSelect: React.FC<IRegionSelect> = ({ value, onChange }) => {
  const { t } = useAppTranslation();
  const { t: regionT } = useAppTranslation("common.regions");

  const options: Option[] = Object.values(EAppLolRegions).map((region) => ({
    label: regionT(region),
    value: region,
  }));

  const renderSelectOption = (option: Option) => {
    const optionDetails = LolRegionHelper.getInfoByCode(
      LolRegionHelper.convertToRegionEnum(option.value),
    );

    return (
      <Group flex="1" gap="xs">
        <Image
          className="inline-block"
          h={20}
          src={optionDetails?.greyImage}
          w={20}
        />{" "}
        {option.label}
      </Group>
    );
  };

  return (
    <Menu>
      <Menu.Target>
        <Indicator
          processing
          classNames={{
            indicator: "before:bg-secret-blue-light bg-secret-blue-light z-10",
          }}
          disabled={!value}
        >
          <Button
            classNames={{
              root: "default-icon border-primary-lighter",
              section: "contents",
            }}
            leftSection={
              <AppThemeIcon className="size-7">
                <Icon.GlobeOutline className="pr-2" />
              </AppThemeIcon>
            }
            radius={8}
            rightSection={<IconChevronDown />}
          >
            {t("common.region")}
          </Button>
        </Indicator>
      </Menu.Target>

      <Menu.Dropdown
        classNames={{
          dropdown: "bg-primary-default border-secret-blue-light p-3",
        }}
      >
        <Checkbox.Group value={[value]}>
          {options.map((option) => {
            return (
              <Checkbox
                onClick={() => onChange(option.value)}
                key={option.value}
                labelPosition="left"
                classNames={{
                  root: "cursor-pointer hover:bg-primary-background p-1 rounded-md",
                  label: "cursor-pointer p-1",
                  labelWrapper: "w-full",
                  body: "py-1 text-white",
                  input: "!bg-transparent border-none",
                  inner: "my-auto",
                }}
                iconColor="white"
                label={renderSelectOption(option)}
                value={option.value}
              />
            );
          })}
        </Checkbox.Group>
      </Menu.Dropdown>
    </Menu>
  );
};

export default RegionRankedSelect;
